import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Input, TextArea } from '../../ui/Input';
import { Select } from '../../ui/Select';
import { FileUpload } from '../../ui/FileUpload';
import { Badge } from '../../ui/Badge';
import { useAssessment } from '../../../hooks/useAssessment';
import { NIST_OUTCOMES, NIST_EVIDENCE_TYPES } from '../../../utils/nistAiRmf';
import { NistFunction } from '../../../types/assessment';
import { FileText, Plus, X, CheckCircle, ArrowRight, ArrowLeft } from 'lucide-react';
interface NistFunctionPageProps {
  functionId: NistFunction;
  title: string;
  description: string;
  nextPath: string;
  prevPath?: string;
}
export function NistFunctionPage({
  functionId,
  title,
  description,
  nextPath,
  prevPath
}: NistFunctionPageProps) {
  const navigate = useNavigate();
  const {
    assessment,
    addEvidence
  } = useAssessment();
  const [isAdding, setIsAdding] = useState(false);
  // Filter outcomes for this function
  const outcomes = NIST_OUTCOMES.filter(o => o.function === functionId);
  // Form State
  const [file, setFile] = useState<File | null>(null);
  const [type, setType] = useState(NIST_EVIDENCE_TYPES[0]);
  const [owner, setOwner] = useState('');
  const [relevance, setRelevance] = useState('');
  const [selectedOutcomes, setSelectedOutcomes] = useState<string[]>([]);
  const handleFileSelect = (selectedFile: File) => {
    setFile(selectedFile);
  };
  const handleOutcomeToggle = (id: string) => {
    setSelectedOutcomes(prev => prev.includes(id) ? prev.filter(o => o !== id) : [...prev, id]);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    addEvidence({
      id: crypto.randomUUID(),
      fileName: file.name,
      type,
      owner,
      dateUploaded: new Date().toISOString(),
      relevance,
      linkedObligations: selectedOutcomes // Reusing this field for NIST outcomes
    });
    // Reset form
    setFile(null);
    setOwner('');
    setRelevance('');
    setSelectedOutcomes([]);
    setIsAdding(false);
  };
  // Calculate progress for this function
  const totalOutcomes = outcomes.length;
  const coveredOutcomes = outcomes.filter(o => assessment.evidence.some(e => e.linkedObligations.includes(o.id))).length;
  const progress = Math.round(coveredOutcomes / totalOutcomes * 100);
  return <div className="max-w-5xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-67">
      <div className="flex items-center justify-between mb-8" data-id="element-68">
        <div data-id="element-69">
          <h1 className="text-3xl font-bold mb-3" data-id="element-70">{title} Function</h1>
          <p className="text-slate-400 text-lg" data-id="element-71">{description}</p>
        </div>
        <div className="text-right" data-id="element-72">
          <div className="text-sm text-slate-400 mb-1" data-id="element-73">Function Alignment</div>
          <div className="text-2xl font-bold text-teal-400" data-id="element-74">{progress}%</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-75">
        {/* Left Column: Outcomes & Evidence List */}
        <div className="lg:col-span-2 space-y-6" data-id="element-76">
          {/* Outcomes List */}
          <div className="space-y-4" data-id="element-77">
            <h3 className="text-lg font-semibold text-white" data-id="element-78">
              Outcomes to Address
            </h3>
            {outcomes.map(outcome => {
            const linkedEvidence = assessment.evidence.filter(e => e.linkedObligations.includes(outcome.id));
            const isCovered = linkedEvidence.length > 0;
            return <Card key={outcome.id} className={`transition-colors ${isCovered ? 'border-teal-500/30 bg-teal-500/5' : 'border-slate-700'}`} data-id="element-79">
                  <div className="flex items-start justify-between" data-id="element-80">
                    <div className="flex-1" data-id="element-81">
                      <div className="flex items-center space-x-2 mb-2" data-id="element-82">
                        <span className="font-mono text-sm font-semibold text-teal-500" data-id="element-83">
                          {outcome.id}
                        </span>
                        {isCovered && <Badge variant="teal" data-id="element-84">Evidence Linked</Badge>}
                      </div>
                      <h4 className="text-white font-medium mb-1" data-id="element-85">
                        {outcome.title}
                      </h4>
                      <p className="text-sm text-slate-400 mb-3" data-id="element-86">
                        {outcome.description}
                      </p>

                      {isCovered && <div className="mt-3 pl-3 border-l-2 border-slate-700 space-y-2" data-id="element-87">
                          {linkedEvidence.map(ev => <div key={ev.id} className="flex items-center text-xs text-slate-300" data-id="element-88">
                              <FileText className="h-3 w-3 mr-2 text-slate-500" data-id="element-89" />
                              {ev.fileName}
                            </div>)}
                        </div>}
                    </div>
                    {isCovered ? <CheckCircle className="h-5 w-5 text-teal-500 flex-shrink-0 ml-4" data-id="element-90" /> : <div className="h-5 w-5 rounded-full border-2 border-slate-700 flex-shrink-0 ml-4" data-id="element-91" />}
                  </div>
                </Card>;
          })}
          </div>
        </div>

        {/* Right Column: Upload Form */}
        <div className="lg:col-span-1" data-id="element-92">
          <div className="sticky top-24 space-y-6" data-id="element-93">
            <Card className={isAdding ? 'border-teal-500 ring-1 ring-teal-500/20' : ''} data-id="element-94">
              <div className="flex items-center justify-between mb-4" data-id="element-95">
                <h3 className="font-semibold text-white" data-id="element-96">Upload Evidence</h3>
                {isAdding && <button onClick={() => setIsAdding(false)} className="text-slate-400 hover:text-white" data-id="element-97">
                    <X className="h-5 w-5" data-id="element-98" />
                  </button>}
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" data-id="element-99">
                <FileUpload onFileSelect={f => {
                setFile(f);
                setIsAdding(true);
              }} label={file ? file.name : 'Drop file or click'} data-id="element-100" />

                {isAdding && <div className="animate-in fade-in slide-in-from-top-2 duration-300 space-y-4" data-id="element-101">
                    <Select label="Evidence Type" options={NIST_EVIDENCE_TYPES.map(t => ({
                  value: t,
                  label: t
                }))} value={type} onChange={e => setType(e.target.value)} data-id="element-102" />

                    <Input label="Owner" placeholder="e.g. Risk Committee" value={owner} onChange={e => setOwner(e.target.value)} required data-id="element-103" />

                    <TextArea label="Relevance / Description" placeholder="How does this support the outcomes?" rows={2} value={relevance} onChange={e => setRelevance(e.target.value)} required data-id="element-104" />

                    <div data-id="element-105">
                      <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-106">
                        Link to Outcomes
                      </label>
                      <div className="space-y-2 max-h-60 overflow-y-auto pr-2 custom-scrollbar bg-navy-900 rounded-md p-2 border border-slate-700" data-id="element-107">
                        {outcomes.map(outcome => <label key={outcome.id} className="flex items-start space-x-2 p-2 rounded hover:bg-navy-800 cursor-pointer" data-id="element-108">
                            <input type="checkbox" className="mt-1 rounded border-slate-600 bg-navy-900 text-teal-500 focus:ring-teal-500" checked={selectedOutcomes.includes(outcome.id)} onChange={() => handleOutcomeToggle(outcome.id)} data-id="element-109" />
                            <span className="text-xs text-slate-300" data-id="element-110">
                              <span className="font-semibold text-teal-400" data-id="element-111">
                                {outcome.id}
                              </span>
                              : {outcome.title}
                            </span>
                          </label>)}
                      </div>
                    </div>

                    <Button type="submit" className="w-full" disabled={!file || !owner || selectedOutcomes.length === 0} data-id="element-112">
                      Save Evidence
                    </Button>
                  </div>}
              </form>
            </Card>

            <div className="flex justify-between pt-4" data-id="element-113">
              {prevPath ? <Button variant="ghost" onClick={() => navigate(prevPath)} data-id="element-114">
                  <ArrowLeft className="h-4 w-4 mr-2" data-id="element-115" /> Back
                </Button> : <div data-id="element-116" />}
              <Button onClick={() => navigate(nextPath)} data-id="element-117">
                Continue <ArrowRight className="h-4 w-4 ml-2" data-id="element-118" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}